import { ScaleLinear } from 'd3-scale';

import { scaleLinear } from '@visx/scale';

import { getMaxStackedValue, getMaxValue, getMinStackedValue, getMinValue } from '.';
import { ChartData, GroupedChartData } from '../types';

const getValueScaleDomain = (
  data: GroupedChartData<ChartData>,
  stacked: boolean,
  axis: 'left' | 'right',
  padding: number,
  numTicks?: number,
  precision?: number,
) => {
  const min = stacked ? getMinStackedValue(data, { axis }) : getMinValue(data, { axis });
  const max = stacked ? getMaxStackedValue(data, { axis }) : getMaxValue(data, { axis });
  const domain = [min < 0 ? min + min * padding : 0, max + max * padding];
  const minDomainSize =
    precision !== undefined && numTicks !== undefined ? precision * numTicks : 0;
  if (domain[1] - domain[0] < minDomainSize) {
    // Enforce minimum domain size so that the ticks don't get forced to be more frequent than the precision
    // This is so that we aren't forced to display fractions for ticks when charting integers
    const diff = minDomainSize - (domain[1] - domain[0]);
    return [domain[0] < 0 ? domain[0] - diff / 2 : domain[0], domain[0] + minDomainSize];
  }
  return domain;
};

export const getValueScale = (
  data: GroupedChartData<ChartData>,
  stacked: boolean,
  axis: 'left' | 'right',
  padding: number,
  range: [number, number],
  numTicks?: number,
  precision?: number,
) => {
  return scaleLinear<number>({
    domain: getValueScaleDomain(data, stacked, axis, padding, numTicks, precision),
    range,
  });
};

/**
 * Given a domain value on scale fromScale, convert it to a domain value on scale toScale.
 */
const convertValueToScale = (
  value: number,
  fromScale: ScaleLinear<number, number>,
  toScale: ScaleLinear<number, number>,
) => toScale.invert(fromScale(value));

/**
 * Given a scale fromScale, convert its ticks to scale toScale.
 */
export const convertTicksToScale = (
  ticks: number[],
  fromScale: ScaleLinear<number, number>,
  toScale: ScaleLinear<number, number>,
) => ticks.map((tick) => convertValueToScale(tick, fromScale, toScale));

/**
 * Adjust the domain of scale so that its 0-position aligns with a tick from alignWithScale.
 * The domain is only stretched to ensure that the data still fits within the scale.
 * Returns the adjusted scale. Noop if the domain doesn't contain zero.
 */
export const alignScale = (
  scale: ScaleLinear<number, number>,
  alignWithScale: ScaleLinear<number, number>,
  numTicks: number,
  precision?: number,
) => {
  const domain = scale.domain();
  const domainCrossesZero = domain[0] <= 0 && domain[1] >= 0;
  const domainSize = domain[1] - domain[0];
  const initialTicks = convertTicksToScale(alignWithScale.ticks(numTicks), alignWithScale, scale);
  let closestTickToZero: number | undefined = initialTicks.reduce((closest, tick) => {
    return Math.abs(tick) < Math.abs(closest) ? tick : closest;
  }, Infinity);

  const adjustedDomain = scale.domain();

  if (domainCrossesZero && closestTickToZero !== 0) {
    // Edge case: can't move closest tick towards zero if it's the first or last tick and we would shrink the domain instead of stretching it
    if (closestTickToZero === initialTicks.at(0) && closestTickToZero < 0) {
      closestTickToZero = initialTicks.at(1);
    } else if (closestTickToZero === initialTicks.at(-1) && closestTickToZero > 0) {
      closestTickToZero = initialTicks.at(-2);
    }
    if (closestTickToZero === undefined) {
      // Less than 3 ticks?
      return scale;
    }

    if (closestTickToZero < 0) {
      // Stretch positive end of domain by the amount that aligns the closest tick to zero
      const domainStretchCoef = domain[0] / (domain[0] - closestTickToZero);
      const domainStretchAmount = domainSize * domainStretchCoef - domainSize;
      adjustedDomain[1] += domainStretchAmount;
    } else if (closestTickToZero > 0) {
      // Stretch negative end of domain by the amount that aligns the closest tick to zero
      const domainStretchCoef = domain[1] / (domain[1] - closestTickToZero);
      const domainStretchAmount = domainSize * domainStretchCoef - domainSize;
      adjustedDomain[0] -= domainStretchAmount;
    }
  }

  // Now that we know that a tick is at zero, we can expand both ends of the domain outwards so
  // they line up with precision.
  // This is useful for integer scales, where we want to avoid displaying fractions.
  // If there is no zero-tick, we just use the middle one.
  const adjustedTicks = convertTicksToScale(
    alignWithScale.ticks(numTicks),
    alignWithScale,
    scale.copy().domain(adjustedDomain),
  );
  const adjustedTickSize = Math.abs((adjustedTicks.at(1) ?? 0) - (adjustedTicks.at(0) ?? 0));

  if (precision !== undefined && precision !== 0 && adjustedTickSize > 0) {
    const middleTickIndex = domainCrossesZero
      ? initialTicks.indexOf(closestTickToZero)
      : Math.floor(initialTicks.length / 2);
    const middleTick = adjustedTicks[middleTickIndex];
    const roundedTickSize = Math.ceil(adjustedTickSize / precision) * precision;

    const domainShiftAmount = -Math.abs(middleTick % precision); // Should be 0 if there is a zero tick
    const domainStretchCoef = roundedTickSize / adjustedTickSize;

    adjustedDomain[0] += domainShiftAmount;

    // Stretch domain outwards from the zero tick, use any tick (middle) if there is no zero
    adjustedDomain[0] = middleTick - (middleTick - adjustedDomain[0]) * domainStretchCoef;
    adjustedDomain[1] = middleTick + (adjustedDomain[1] - middleTick) * domainStretchCoef;
  }

  return scale.copy().domain(adjustedDomain);
};
